import { apiClient } from "../apiClient";
import {
  AnalyticsSavedFilterDto,
  AnalyticsSavedFilterVisibility,
} from "../../types/analytics/AnalyticsSavedFilter";

export type SavedFilterApiError = {
  message: string;
  error?: string;
  status?: number;
};

function isNameConflict(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as SavedFilterApiError;
  return e.error === "NAME_CONFLICT" || e.status === 409;
}

async function savedFilterFetch(
  accessToken: string | undefined,
  endpoint: string,
  options?: RequestInit
): Promise<any> {
  const impersonationSessionId = sessionStorage.getItem(
    "impersonationSessionId"
  );
  const response = await window.fetch(
    `${process.env.REACT_APP_API_URL}/${endpoint}`,
    {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        ...(impersonationSessionId
          ? { "X-Impersonation-Session": impersonationSessionId }
          : {}),
        ...(options?.headers ?? {}),
      },
    }
  );

  if (response.ok) {
    if (response.status === 204) return null;
    return response.json();
  }

  let message = response.statusText;
  let error: string | undefined;
  try {
    const body = await response.json();
    if (body?.message && typeof body.message === "string") {
      message = body.message;
    }
    if (body?.error && typeof body.error === "string") {
      error = body.error;
    }
  } catch {
    // keep statusText
  }

  const err: SavedFilterApiError = {
    message,
    error,
    status: response.status,
  };
  return Promise.reject(err);
}

class analyticsSavedFiltersApi {
  constructor(authToken: string | undefined) {
    this.accessToken = authToken;
  }

  accessToken: string | undefined;

  list(
    pageKey: string,
    pageId: number | undefined,
    onSuccess: (data: AnalyticsSavedFilterDto[]) => void,
    onError: (error: any) => void
  ) {
    let url = `analytics/saved-filters?pageKey=${encodeURIComponent(pageKey)}`;
    if (pageId != null) {
      url += `&pageId=${pageId}`;
    }
    return apiClient(this.accessToken, url).then(
      (data) => onSuccess((data as AnalyticsSavedFilterDto[]) ?? []),
      (error) => onError(error)
    );
  }

  get(
    id: number,
    pageId: number | undefined,
    onSuccess: (data: AnalyticsSavedFilterDto) => void,
    onError: (error: SavedFilterApiError) => void
  ) {
    let url = `analytics/saved-filters/${id}`;
    if (pageId != null) {
      url += `?pageId=${pageId}`;
    }
    return savedFilterFetch(this.accessToken, url).then(
      (data) => onSuccess(data as AnalyticsSavedFilterDto),
      (error) => onError(error)
    );
  }

  create(
    body: {
      name: string;
      visibility: AnalyticsSavedFilterVisibility;
      filterJson: string;
      pageKey: string;
    },
    onSuccess: (data: AnalyticsSavedFilterDto) => void,
    onError: (error: SavedFilterApiError) => void
  ) {
    return savedFilterFetch(this.accessToken, "analytics/saved-filters", {
      method: "POST",
      body: JSON.stringify(body),
    }).then(
      (data) => onSuccess(data as AnalyticsSavedFilterDto),
      (error) => onError(error)
    );
  }

  update(
    id: number,
    body: {
      name: string;
      visibility: AnalyticsSavedFilterVisibility;
      filterJson: string;
      pageKey: string;
    },
    onSuccess: (data: AnalyticsSavedFilterDto) => void,
    onError: (error: SavedFilterApiError) => void
  ) {
    return savedFilterFetch(this.accessToken, `analytics/saved-filters/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }).then(
      (data) => onSuccess(data as AnalyticsSavedFilterDto),
      (error) => onError(error)
    );
  }

  rename(
    id: number,
    name: string,
    pageId: number | undefined,
    onSuccess: (data: AnalyticsSavedFilterDto) => void,
    onError: (error: SavedFilterApiError) => void
  ) {
    let url = `analytics/saved-filters/${id}/name`;
    if (pageId != null) {
      url += `?pageId=${pageId}`;
    }
    return savedFilterFetch(this.accessToken, url, {
      method: "PUT",
      body: JSON.stringify({ name }),
    }).then(
      (data) => onSuccess(data as AnalyticsSavedFilterDto),
      (error) => onError(error)
    );
  }

  delete(
    id: number,
    onSuccess: () => void,
    onError: (error: any) => void
  ) {
    return apiClient(this.accessToken, `analytics/saved-filters/${id}`, {
      method: "DELETE",
    }).then(
      () => onSuccess(),
      (error) => onError(error)
    );
  }

  setPageDefault(
    pageId: number,
    savedFilterId: number | null,
    onSuccess: () => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/pages/${pageId}/default-saved-filter`,
      {
        method: "PUT",
        body: JSON.stringify({ savedFilterId }),
      }
    ).then(
      () => onSuccess(),
      (error) => onError(error)
    );
  }
}

export { isNameConflict };
export default analyticsSavedFiltersApi;
